import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { logoutUser, getUser } from "../../redux/authSlice";
import { getCurrentTherapist, logoutTherapist } from "../../redux/therapistSlice";
import { selectTotalUnread } from "../../redux/chatSlice";
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  IconButton,
  Popover,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Drawer,
  Badge,
  Collapse,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import HomeIcon from "@mui/icons-material/Home";
import InfoIcon from "@mui/icons-material/Info";
import GroupIcon from "@mui/icons-material/Group";
import PersonIcon from "@mui/icons-material/Person";
import PeopleIcon from "@mui/icons-material/People";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ExploreIcon from "@mui/icons-material/Explore";
import SpaIcon from "@mui/icons-material/Spa";
import { useTranslation } from "react-i18next";
import { HashLink } from "react-router-hash-link";
import { useNavigate, useLocation, Link } from "react-router-dom";
import Logo from "../../assets/image/header/mindtherapiyLogo.svg";
import { FaRegNewspaper, FaInfoCircle } from "react-icons/fa";
import { MdLiveHelp } from "react-icons/md";
import { LANDING_PAGES } from "../../pages/LandingPages/landingPageConfigs";

const Header = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const [language] = useState(
    localStorage.getItem("language") || "EN"
  );
  const [menuOpen, setMenuOpen] = useState(false);
  const [therapyOpen, setTherapyOpen] = useState(false);
  const [exploreOpen, setExploreOpen] = useState(false);
  const [loginAnchorEl, setLoginAnchorEl] = useState(null);
  const [signUpAnchorEl, setSignUpAnchorEl] = useState(null);
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState(null);
  const [therapyAnchorEl, setTherapyAnchorEl] = useState(null);
  const [exploreAnchorEl, setExploreAnchorEl] = useState(null);
  
  // Redux state'leri
  const user = useSelector((state) => state.auth.user);
  const currentTherapist = useSelector((state) => state.therapist?.therapist);
  const totalUnread = useSelector(selectTotalUnread);
  const navigate = useNavigate();
  const location = useLocation();
  const isLandingPage = location.pathname.startsWith("/therapy/");

  // LocalStorage'dan bilgileri al
  const userType = localStorage.getItem("userType");
  const token = localStorage.getItem("token");
  
  // UserType'a göre mevcut kullanıcıyı belirle
  const getCurrentUser = () => {
    if (userType === "therapist" && currentTherapist) {
      return {
        user: currentTherapist,
        type: "therapist",
        userName: currentTherapist.name || currentTherapist.fullName
      };
    } else if (userType === "admin" && user) {
      return {
        user: user,
        type: "admin",
        userName: user.name || user.fullName
      };
    } else if (user) {
      return {
        user: user,
        type: userType || 'user',
        userName: user.name || user.fullName
      };
    } else if (token && userType) {
      return {
        user: null,
        type: userType,
        userName: null
      };
    }
    
    return {
      user: null,
      type: null,
      userName: null
    };
  };

  const { user: currentUser, type: currentType, userName } = getCurrentUser();

  const HEADER_HEIGHT = 86;
  const [isScrolled, setIsScrolled] = useState(false);

  // Component mount edildiğinde kullanıcı bilgilerini getir
  useEffect(() => {
    const fetchUserData = async () => {
      if (token && !currentUser) {
        try {
          if (userType === "therapist") {
            console.log("Therapist bilgileri getiriliyor...");
            await dispatch(getCurrentTherapist());
          } else if (userType === "user" || userType === "admin") {
            console.log("User bilgileri getiriliyor...");
            await dispatch(getUser());
          }
        } catch (error) {
          console.error("Kullanıcı bilgileri getirilemedi:", error);
        }
      }
    };

    fetchUserData();
  }, [dispatch, token, userType, currentUser]);



  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    i18n.changeLanguage(language);
  }, [language, i18n]);

  const toggleMenu = () => {
    setMenuOpen((prev) => !prev);
  };

  const navigateToHowItWorks = () => {
    navigate("/how-does-it-work");
    setMenuOpen(false);
  };

  const handleLoginMenuOpen = (event) => {
    setLoginAnchorEl(event.currentTarget);
  };

  const handleLoginMenuClose = () => {
    setLoginAnchorEl(null);
  };

  const handleSignUpMenuOpen = (event) => {
    setSignUpAnchorEl(event.currentTarget);
  };

  const handleSignUpMenuClose = () => {
    setSignUpAnchorEl(null);
  };

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
  };

  const handleProfileClick = () => {
    // Type'a göre yönlendirme yap
    switch (currentType) {
      case "admin":
        navigate("/adminHome");
        break;
      case "therapist":
        navigate("/therapistHomePage");
        break;
      case "user":
      default:
        navigate("/userHomePage");
        break;
    }
    handleUserMenuClose();
  };

  const handleLogout = () => {
    const normalizedType = (currentType || localStorage.getItem("userType") || "").toLowerCase();
    const redirectPath = normalizedType === "therapist" ? "/signInTherapist" : "/signIn";
    
    // UserType'a göre doğru logout fonksiyonunu çağır
    if (normalizedType === "therapist") {
      dispatch(logoutTherapist());
    } else {
      dispatch(logoutUser());
    }
    
    // LocalStorage'ı temizle
    localStorage.removeItem("userType");
    
    handleUserMenuClose();
    window.location.replace(redirectPath); // force reload + redirect to correct sign-in
  };

  // Type'a göre profil sayfası adını belirle
  const getProfilePageName = () => {
    switch (currentType) {
      case "admin":
        return t("adminPanel") || "Admin Panel";
      case "therapist":
        return t("therapistPanel") || "Terapist Paneli";
      case "user":
      default:
        return t("profilePage") || "Profil Sayfası";
    }
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        backgroundColor: (isScrolled || isLandingPage) ? "#f4efea" : "transparent",
        boxShadow: "none",
        borderRadius: "50px",
        marginTop: "5px",
        height: `${HEADER_HEIGHT}px`,
        zIndex: 1100,
        transition: "background-color 0.3s ease",
      }}
    >
      <Toolbar
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "auto 1fr auto" },
          alignItems: "center",
          padding: "0rem 2rem",
          minHeight: `${HEADER_HEIGHT}px`,
          backgroundColor: "transparent",
          position: "relative",
          columnGap: { md: 4 },
        }}
      >
        {/* Hamburger Menu (Small Screens) */}
        <Box
          sx={{
            display: { xs: "block", md: "none" },
            position: "absolute",
            left: "16px",
            top: "16px",
          }}
        >
          <IconButton onClick={toggleMenu} sx={{ color: "#212529" }}>
            <MenuIcon />
          </IconButton>
        </Box>

        <Drawer
          anchor="top"
          open={menuOpen}
          onClose={toggleMenu}
          sx={{
            "& .MuiDrawer-paper": {
              width: { xs: "90vw", sm: "70vw" },
              margin: "auto",
              borderRadius: "20px",
              backgroundColor: "#f4efea",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "flex-start",
              padding: "20px",
              marginTop: "2vh",
              maxHeight: "85vh",
              height: "auto",
              overflowY: "auto",
              overflowX: "hidden",
              WebkitOverflowScrolling: "touch",
            },
          }}
        >
         <List sx={{ width: "100%" }}>
  {/* Home */}
  <ListItem button onClick={() => { navigate("/"); setMenuOpen(false); }}>
    <ListItemIcon sx={{ minWidth: "40px" }}><HomeIcon /></ListItemIcon>
    <ListItemText primary={t("home")} />
  </ListItem>
  <Divider />

  {/* Therapy — accordion */}
  <ListItem button onClick={() => setTherapyOpen((p) => !p)}>
    <ListItemIcon sx={{ minWidth: "40px" }}><SpaIcon /></ListItemIcon>
    <ListItemText primary="Therapy" />
    {therapyOpen ? <ExpandLess /> : <ExpandMore />}
  </ListItem>
  <Collapse in={therapyOpen} timeout="auto" unmountOnExit>
    <List component="div" disablePadding>
      {LANDING_PAGES.map((page) => (
        <React.Fragment key={page.slug}>
          <ListItem
            button
            sx={{ pl: 6 }}
            onClick={() => { navigate(`/therapy/${page.slug}`); setMenuOpen(false); }}
          >
            <ListItemIcon sx={{ minWidth: "36px" }}><GroupIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary={page.title} primaryTypographyProps={{ fontSize: 14 }} />
          </ListItem>
          <Divider sx={{ ml: 6 }} />
        </React.Fragment>
      ))}
    </List>
  </Collapse>
  <Divider />

  {/* Explore — accordion */}
  <ListItem button onClick={() => setExploreOpen((p) => !p)}>
    <ListItemIcon sx={{ minWidth: "40px" }}><ExploreIcon /></ListItemIcon>
    <ListItemText primary="Explore" />
    {exploreOpen ? <ExpandLess /> : <ExpandMore />}
  </ListItem>
  <Collapse in={exploreOpen} timeout="auto" unmountOnExit>
    <List component="div" disablePadding>
      <ListItem button sx={{ pl: 6 }} onClick={() => { navigateToHowItWorks(); setMenuOpen(false); }}>
        <ListItemIcon sx={{ minWidth: "36px" }}><InfoIcon fontSize="small" /></ListItemIcon>
        <ListItemText primary={t("howDoesItWork")} primaryTypographyProps={{ fontSize: 14 }} />
      </ListItem>
      <Divider sx={{ ml: 6 }} />
      <ListItem button sx={{ pl: 6 }} onClick={() => { setMenuOpen(false); window.location.href = "/#native-language"; }}>
        <ListItemIcon sx={{ minWidth: "36px" }}><PeopleIcon fontSize="small" /></ListItemIcon>
        <ListItemText primary={t("nativeLanguage")} primaryTypographyProps={{ fontSize: 14 }} />
      </ListItem>
      <Divider sx={{ ml: 6 }} />
      <ListItem button sx={{ pl: 6 }} onClick={() => { setMenuOpen(false); window.location.href = "/#our-expert-staff"; }}>
        <ListItemIcon sx={{ minWidth: "36px" }}><GroupIcon fontSize="small" /></ListItemIcon>
        <ListItemText primary={t("ourExpertStaff")} primaryTypographyProps={{ fontSize: 14 }} />
      </ListItem>
    </List>
  </Collapse>
  <Divider />

  {/* Blog */}
  <ListItem button onClick={() => { navigate("/blog"); setMenuOpen(false); }}>
    <ListItemIcon sx={{ minWidth: "40px" }}><FaRegNewspaper /></ListItemIcon>
    <ListItemText primary={t("blog")} />
  </ListItem>
  <Divider />

  {/* FAQ */}
  <ListItem button onClick={() => { navigate("/faq"); setMenuOpen(false); }}>
    <ListItemIcon sx={{ minWidth: "40px" }}><MdLiveHelp /></ListItemIcon>
    <ListItemText primary={t("FAQ")} />
  </ListItem>
  <Divider />

  {/* About Us */}
  <ListItem button onClick={() => { navigate("/aboutUs"); setMenuOpen(false); }}>
    <ListItemIcon sx={{ minWidth: "40px" }}><FaInfoCircle /></ListItemIcon>
    <ListItemText primary={t("aboutUs")} />
  </ListItem>
  <Divider />

  {/* Login ve Signup Butonlarını Yan Yana Koy */}
  <Box sx={{ display: "flex", justifyContent: "center", gap: "12px", padding: "15px 0px" }}>
    {userName ? (
      <>
        <Badge
          badgeContent={totalUnread}
          max={99}
          color="error"
          overlap="circular"
        >
          <Button
            onClick={handleUserMenuOpen}
            sx={{
              color: "#625480",
              borderColor: "#625480",
              borderRadius: "20px",
              padding: "6px 16px",
              textTransform: "none",
              border: "1px solid #625480",
              backgroundColor: "#ffffff",
              "&:hover": {
                backgroundColor: "#625480",
                color: "white",
              },
            }}
          >
            <Box sx={{ fontSize: "14px", fontWeight: "bold" }}>{userName}</Box>
          </Button>
        </Badge>
        <Popover
          open={Boolean(userMenuAnchorEl)}
          anchorEl={userMenuAnchorEl}
          onClose={handleUserMenuClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          transformOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <List sx={{ width: "200px", p: 1 }}>
            <ListItem button onClick={handleProfileClick}>
              <ListItemText primary={getProfilePageName()} />
            </ListItem>
            <Divider />
            <ListItem button onClick={handleLogout}>
              <ListItemText primary={t("logout")} />
            </ListItem>
          </List>
        </Popover>
      </>
    ) : (
      <>
        <Button
          variant="outlined"
          onClick={handleLoginMenuOpen}
          sx={{
            color: "#625480",
            borderColor: "#625480",
            borderRadius: "20px",
            padding: "6px 16px",
            textTransform: "capitalize",
            "&:hover": {
              backgroundColor: "#625480",
              color: "white"
            },
          }}
        >
          {t("login")}
        </Button>
        <Button
          variant="contained"
          sx={{
            backgroundColor: "#625480",
            color: "#ffffff",
            borderRadius: "20px",
            padding: "6px 16px",
            fontFamily: "Orelega One",
            textTransform: "capitalize",
            "&:hover": {
              backgroundColor: "#625480",
              color: "white"
            },
          }}
          onClick={handleSignUpMenuOpen}
        >
          {t("signUp")}
        </Button>
      </>
    )}
  </Box>

  </List>
        </Drawer>

        {/* Logo */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: { xs: "center", md: "flex-start" },
            width: { xs: "100%", md: "auto" },
            paddingLeft: { xs: "48px", md: 0 },
            paddingRight: { xs: "48px", md: 0 },
          }}
        >
          <Link to="/" style={{ display: "flex", alignItems: "center" }}>
            <img
              src={Logo}
              alt="Therhappy Logo"
              style={{
                width: "200px",
                maxWidth: "100%",
              }}
            />
          </Link>
        </Box>

        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            gap: { md: 1, lg: 2 },
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          {/* Therapy dropdown */}
          <Button
            variant="text"
            sx={{
              fontSize: { lg: "24px", md: "15px" },
              color: "#2d465e",
              textTransform: "capitalize",
              whiteSpace: "nowrap",
              minWidth: "auto",
              px: { md: 1, lg: 1.5 },
            }}
            onClick={(e) => setTherapyAnchorEl(e.currentTarget)}
          >
            Therapy ▾
          </Button>
          <Popover
            open={Boolean(therapyAnchorEl)}
            anchorEl={therapyAnchorEl}
            onClose={() => setTherapyAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            transformOrigin={{ vertical: "top", horizontal: "center" }}
            disableScrollLock
            sx={{ marginTop: "8px" }}
          >
            <List sx={{ width: "220px", p: 1 }}>
              {LANDING_PAGES.map((page) => (
                <React.Fragment key={page.slug}>
                  <ListItem
                    button
                    onClick={() => {
                      navigate(`/therapy/${page.slug}`);
                      setTherapyAnchorEl(null);
                    }}
                  >
                    <ListItemText primary={page.title} />
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
            </List>
          </Popover>

          {/* Explore dropdown — How It Works, Native Language, Expert Staff */}
          <Button
            variant="text"
            sx={{
              fontSize: { lg: "24px", md: "15px" },
              color: "#2d465e",
              textTransform: "capitalize",
              whiteSpace: "nowrap",
              minWidth: "auto",
              px: { md: 1, lg: 1.5 },
            }}
            onClick={(e) => setExploreAnchorEl(e.currentTarget)}
          >
            Explore ▾
          </Button>
          <Popover
            open={Boolean(exploreAnchorEl)}
            anchorEl={exploreAnchorEl}
            onClose={() => setExploreAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            transformOrigin={{ vertical: "top", horizontal: "center" }}
            disableScrollLock
            sx={{ marginTop: "8px" }}
          >
            <List sx={{ width: "220px", p: 1 }}>
              <ListItem button onClick={() => { navigateToHowItWorks(); setExploreAnchorEl(null); }}>
                <ListItemText primary={t("howDoesItWork")} />
              </ListItem>
              <Divider />
              <ListItem button onClick={() => { setExploreAnchorEl(null); window.location.href = "/#native-language"; }}>
                <ListItemText primary={t("nativeLanguage")} />
              </ListItem>
              <Divider />
              <ListItem button onClick={() => { setExploreAnchorEl(null); window.location.href = "/#our-expert-staff"; }}>
                <ListItemText primary={t("ourExpertStaff")} />
              </ListItem>
            </List>
          </Popover>

          {/* Blog */}
          <HashLink smooth to="/blog" style={{ textDecoration: "none" }}>
            <Button
              variant="text"
              sx={{
                fontSize: { lg: "24px", md: "15px" },
                color: "#2d465e",
                textTransform: "capitalize",
                whiteSpace: "nowrap",
                minWidth: "auto",
                px: { md: 1, lg: 1.5 },
              }}
            >
              {t("blog")}
            </Button>
          </HashLink>

          {/* FAQ */}
          <HashLink smooth to="/faq" style={{ textDecoration: "none" }}>
            <Button
              variant="text"
              sx={{
                fontSize: { lg: "24px", md: "15px" },
                color: "#2d465e",
                textTransform: "capitalize",
                whiteSpace: "nowrap",
                minWidth: "auto",
                px: { md: 1, lg: 1.5 },
              }}
            >
              {t("FAQ")}
            </Button>
          </HashLink>

          {/* About Us */}
          <HashLink smooth to="/aboutUs" style={{ textDecoration: "none" }}>
            <Button
              variant="text"
              sx={{
                fontSize: { lg: "24px", md: "15px" },
                color: "#2d465e",
                textTransform: "capitalize",
                whiteSpace: "nowrap",
                minWidth: "auto",
                px: { md: 1, lg: 1.5 },
              }}
            >
              {t("aboutUs")}
            </Button>
          </HashLink>
        </Box>

        {/* Language & Auth Buttons */}
        <Box sx={{ display: { xs: "none", md: "flex" }, gap: 2, marginLeft: "auto", alignItems: "center", height: "100%" }}>
          {/* Giriş Yap Butonu */}
          {userName ? (
            <>
              <Badge
                badgeContent={totalUnread}
                max={99}
                color="error"
                overlap="circular"
              >
                <Button
                  onClick={handleUserMenuOpen}
                  sx={{
                    color: "#625480",
                    borderColor: "#625480",
                    borderRadius: "20px",
                    padding: "4px 12px",
                    border: "1px solid #625480",
                    backgroundColor: "#ffffff",
                    fontSize: { lg: "16px", md: "12px", sm: "8px", xs: "10px" },
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: "#625480",
                      color: "white",
                    },
                    display: "inline-flex",
                    alignItems: "center",
                    minHeight: "auto",
                  }}
                >
                  <Box sx={{ fontWeight: "bold" }}>{userName}</Box>
                </Button>
              </Badge>
              <Popover
                open={Boolean(userMenuAnchorEl)}
                anchorEl={userMenuAnchorEl}
                onClose={handleUserMenuClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                transformOrigin={{ vertical: "top", horizontal: "center" }}
              >
                <List sx={{ width: "200px", p: 1 }}>
                  <ListItem button onClick={handleProfileClick}>
                    <ListItemText primary={getProfilePageName()} />
                  </ListItem>
                  <Divider />
                  <ListItem button onClick={handleLogout}>
                    <ListItemText primary={t("logout")} />
                  </ListItem>
                </List>
              </Popover>
            </>
          ) : (
            <>
              <Button
                variant="outlined"
                onClick={handleLoginMenuOpen}
                sx={{
                  color: "#625480",
                  borderColor: "#625480",
                  borderRadius: "20px",
                  padding: "4px 12px",
                  fontSize: { lg: "16px", md: "12px", sm: "8px", xs: "10px" },
                  textTransform: "capitalize",
                  "&:hover": {
                    backgroundColor: "#625480",
                    color: "white",
                  },
                }}
              >
                {t("login")}
              </Button>
              <Popover
                open={Boolean(loginAnchorEl)}
                anchorEl={loginAnchorEl}
                onClose={handleLoginMenuClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                transformOrigin={{ vertical: "top", horizontal: "center" }}
                sx={{ marginTop: "8px", cursor: "pointer" }}
              >
                <List sx={{ width: "220px", p: 1 }}>
                  <ListItem button onClick={() => navigate("/signIn")}>
                    <ListItemIcon>
                      <PersonIcon sx={{ color: "#625480" }} />
                    </ListItemIcon>
                    <ListItemText primary={t("loginAsUser")} />
                  </ListItem>
                  <Divider />
                  <ListItem button onClick={() => navigate("/signInTherapist")}>
                    <ListItemIcon>
                      <PeopleIcon sx={{ color: "#625480" }} />
                    </ListItemIcon>
                    <ListItemText primary={t("loginAsTherapist")} />
                  </ListItem>
                </List>
              </Popover>
            </>
          )}

          {/* Kayıt Ol Butonu - Sadece kullanıcı giriş yapmadığında göster */}
          {!userName && (
            <>
              <Button
                variant="contained"
                sx={{
                  backgroundImage: "linear-gradient(to right, #625480, #E5D3DD)",
                  color: "#ffffff",
                  borderRadius: "20px",
                  padding: "4px 12px",
                  fontSize: { lg: "16px", md: "12px", sm: "8px", xs: "10px" },
                  textTransform: "capitalize",
                  "&:hover": {
                    backgroundColor: "#625480",
                    color: "white",
                  },
                }}
                onClick={handleSignUpMenuOpen}
              >
                {t("signUp")}
              </Button>

              {/* Signup Açılır Menü */}
              <Popover
                open={Boolean(signUpAnchorEl)}
                anchorEl={signUpAnchorEl}
                onClose={handleSignUpMenuClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "center",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "center",
                }}
                sx={{ marginTop: "8px", cursor: "pointer" }}
              >
                <List sx={{ width: "200px", p: 1 }}>
                  <ListItem button onClick={() => navigate("/signUp")}>
                    <ListItemText primary={t("signUpAsUser")} />
                  </ListItem>
                  <Divider />
                  <ListItem button onClick={() => navigate("/signUpTherapist")}>
                    <ListItemText primary={t("signUpAsTherapist")} />
                  </ListItem>
                </List>
              </Popover>
            </>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
